import { Typography } from '@mui/joy';
import {
  CircularProgress,
  ListItemText,
  MenuItem,
  Select,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { axiosInstanceForTenantAPI } from '../../assets/utils/axiosInstance';
import { useToastNotificationContext } from '../../hooks/global/alert/useToastNotification';
import RESTAPIError from '../../interfaces/RESTAPIError';
import LimitViewReportProps from '../../interfaces/userRoleSettingsProps';
import customerAuthService from '../../services/customerAuthService';

import { ensureHttps } from '~/assets/utils/reportList/urlPaginationHttpsValidation';
import { Checkbox } from '~/components/ui/checkbox';
import { InputField } from '~/components/ui/input';
import { reportReferenceIdSchema } from '~/services/api/users/types';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';
import { useForm } from '~/utils/forms';

interface ReportAccessComponentProps extends LimitViewReportProps {
  loading: boolean;
  isError: boolean;
  mutate: () => void;
  ReportIds: string[];
  type?: 'allow' | 'restrict';
}

interface ReportIdData {
  reference_id: string;
  formatted_report_number: string;
}

export default function ReportAccessComponent({
  ReportIds,
  userId,
  loading,
  isError,
  mutate,
  type = 'allow',
}: ReportAccessComponentProps) {
  const intl = useIntl();
  const channelId = useIntakeChannel().intakeChannelData.selectedChannel ?? '';
  const form = useForm({ schema: reportReferenceIdSchema });
  const isSubmitting = form.formState.isSubmitting;

  const { createToastNotification, openConfirmationModal } =
    useToastNotificationContext();
  const [referenceIds, setReferenceIds] = useState<string[]>([]); //first page of Reference ID's from pagindate API data to display in the list dropwdown
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [reportIdData, setReportIdData] = useState<ReportIdData[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [nextPage, setNextPage] = useState(null);

  useEffect(() => {
    fetchComponentStateFromAPI();
  }, [ReportIds]); //eslint-disable-line

  const fetchComponentStateFromAPI = async () => {
    try {
      setIsLoading(true);
      if (localStorage.getItem('slug')) {
        const customerReportsList = await axiosInstanceForTenantAPI(
          localStorage.getItem('slug'),
        ).get(
          `/report-list?channel_id=${channelId}&department_name=${''}&followed_by_user=${'False'}&only_assigned_reports=${'False'}&ordering=${''}&priority=${''}&tag=${''}&${''}`,
        );

        const filteredData = customerReportsList.data.results.map(
          (report: {
            reference_id: string;
            formatted_report_number: string;
          }) => ({
            reference_id: report.reference_id,
            formatted_report_number: report.formatted_report_number,
          }),
        );
        setReportIdData(filteredData);

        setSelectedReportIds(
          filteredData
            .filter((report: { reference_id: string }) =>
              ReportIds.includes(report.reference_id),
            )
            .map(
              (report: { formatted_report_number: string }) =>
                report.formatted_report_number,
            ),
        );

        setReferenceIds(
          filteredData.map(
            (report: { formatted_report_number: string }) =>
              report.formatted_report_number,
          ),
        );
        setNextPage(customerReportsList.data.next);
        setIsLoading(false);
      }
    } catch (err) {
      const apiError = err as RESTAPIError;
      setErrorMessage(apiError.message);
    }
  };

  const updateComponentStateViaAPI = async () => {
    const selectedIds = reportIdData
      .filter((report: { formatted_report_number: string }) =>
        selectedReportIds.includes(report.formatted_report_number),
      )
      .map((report: { reference_id: string }) => report.reference_id);
    try {
      setIsLoading(true);
      await customerAuthService.updateAllowedSpecificReportAccess(
        userId,
        localStorage.getItem('slug'),
        selectedIds,
        type,
      );

      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'User details successfully updated.',
        }),
        'success',
      );
      setIsLoading(false);
      mutate();
    } catch (err) {
      const apiError = err as RESTAPIError;
      setErrorMessage(apiError.message);
    }
  };

  const fetchNextReportIdsPage = async () => {
    if (nextPage !== null) {
      const newReportList = await axiosInstanceForTenantAPI(
        localStorage.getItem('slug'),
      ).get(ensureHttps(nextPage));
      setNextPage(newReportList.data.next);
      const filteredData = newReportList.data.results.map(
        (report: {
          reference_id: string;
          formatted_report_number: string;
        }) => ({
          reference_id: report.reference_id,
          formatted_report_number: report.formatted_report_number,
        }),
      );
      setReportIdData((prevData) => [...prevData, ...filteredData]);
      const newIds = newReportList.data.results.map(
        (report: { formatted_report_number: string }) =>
          report.formatted_report_number,
      );
      setReferenceIds((prevIds) => [...prevIds, ...newIds]);
    }
  };

  const handleCheckboxChange = (id: string) => () => {
    setSelectedReportIds((prevSelected) => {
      const existingIndex = prevSelected.findIndex((refId) => refId === id);
      if (existingIndex !== -1) {
        // Remove item if already exists
        const updatedSelection = [...prevSelected];
        updatedSelection.splice(existingIndex, 1);
        return updatedSelection;
      } else {
        // Add the item if it doesn't exist
        return [...prevSelected, id];
      }
    });
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    openConfirmationModal(
      intl.formatMessage({
        defaultMessage: 'Confirmation',
      }),
      intl.formatMessage({
        defaultMessage:
          'By restricting access, the user will lose access to their currently assigned reports.',
      }),
      intl.formatMessage({
        defaultMessage: 'Cancel',
      }),
      intl.formatMessage({
        defaultMessage: 'Apply Changes',
      }),
      () => updateComponentStateViaAPI(),
    );
  };

  const onSubmitHandler = form.handleSubmit(async (data) => {
    if (!data.reportReferenceId) return;
    if (selectedReportIds.includes(data.reportReferenceId)) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'Report has already been added.',
        }),
        'error',
      );
      return;
    }
    if (referenceIds.includes(data.reportReferenceId)) {
      setSelectedReportIds((prevSelected) => [
        ...prevSelected,
        data.reportReferenceId,
      ]);
    }

    form.reset();
  });

  // Function that fetches more of the paginated data if once the scroll bar hits the bottom
  const handleScroll = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    // Check if the scroll position is at the bottom
    const isAtBottom =
      target.scrollHeight - Math.ceil(target.scrollTop) === target.clientHeight;
    if (isAtBottom) {
      fetchNextReportIdsPage();
    }
  };

  return (
    <div>
      <div className='mb-6 mt-6 justify-start text-left'>
        <Typography level='title-md'>
          {type === 'allow' ? (
            <FormattedMessage defaultMessage='Grant Report Access' />
          ) : (
            <FormattedMessage defaultMessage='Restrict Report Access' />
          )}
        </Typography>
      </div>
      <div className='mb-4'>
        <Typography
          level='body-sm'
          sx={{
            color: 'grey',
          }}
        >
          {type === 'allow' ? (
            <FormattedMessage defaultMessage='Grant this user access only to specific reports' />
          ) : (
            <FormattedMessage defaultMessage='Restrict this user access to specific reports' />
          )}
        </Typography>
      </div>
      <div className='flex flex-row w-full items-center h-17'>
        <div className='min-w-[151/2] h-10'>
          <Select
            open={isOpen}
            onClose={handleClose}
            onOpen={handleOpen}
            sx={{
              height: '100%',
            }}
            multiple
            value={selectedReportIds}
            renderValue={(selected) => {
              if (selected.length == 0) {
                return (
                  <div className='mt-3 flex flex-row items-center w-full'>
                    <Typography
                      level='body-sm'
                      sx={{ color: 'rgba(135, 135, 135, 1)' }}
                    >
                      <FormattedMessage defaultMessage='Select Report' />
                    </Typography>
                  </div>
                );
              }

              return (
                <div className='mt-3 flex flex-row items-center'>
                  <Typography level='body-sm'>
                    <FormattedMessage defaultMessage='Select Report' />
                  </Typography>
                  <div className='w-4 h-4 bg-[rgba(0,206,207,1)] rounded-full flex items-center justify-center ml-1'>
                    <Typography level='body-sm' sx={{ fontWeight: 700 }}>
                      {selected.length}
                    </Typography>
                  </div>
                </div>
              );
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              PaperProps: {
                style: {
                  marginTop: '4px',
                  maxHeight: '226px', // Set the maximum height for the dropdown menu
                },
                onScroll: handleScroll, // Attach the handleScroll function here
              },
            }}
          >
            <div>
              <form onSubmit={onSubmitHandler}>
                <div className='p-3'>
                  <InputField
                    field={'reportReferenceId'}
                    control={form.control}
                    placeholder='Report Reference Number'
                    disabled={isSubmitting}
                    onChange={(e) => {
                      e.preventDefault(); // Prevent any default behavior
                      form.setValue('reportReferenceId', e.target.value); // Set the form value for reportReferenceId
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
              </form>
            </div>
            {referenceIds
              .filter((formattedNumber) =>
                formattedNumber.includes(
                  form.getValues('reportReferenceId') || '',
                ),
              )
              .map((formattedNumber) => (
                <MenuItem
                  key={formattedNumber}
                  value={formattedNumber}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(240, 240, 240, 1)', // Change background color on hover
                    },
                  }}
                >
                  <Checkbox
                    checked={selectedReportIds.some(
                      (refId) => refId === formattedNumber,
                    )}
                    onChange={handleCheckboxChange(formattedNumber)}
                    sx={{
                      width: '14px',
                      height: '14px',
                      mr: '8px',
                      borderRadius: '1.5px',
                      '&.Mui-checked': {
                        color: 'rgba(0, 206, 207, 1)', // Change checked checkbox color
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '14px',
                      },
                    }}
                  />
                  <ListItemText
                    className='ml-3'
                    onClick={handleCheckboxChange(formattedNumber)}
                    primary={formattedNumber}
                  />
                </MenuItem>
              ))}
          </Select>
        </div>

        {/* Dyanmically generate the boxes with the names of the selected reports that are in the SelectedReports Array */}
        {isLoading || loading ? (
          <CircularProgress
            size={20}
            style={{ marginLeft: '19px', color: 'black' }}
          />
        ) : (
          <div>
            <div className='flex flex-row flex-wrap'>
              {selectedReportIds.map((referenceId, index) => (
                <div
                  className='ml-3 mt-2.5 h-9 border border-gray-400 rounded flex items-center justify-center py-2 px-3'
                  key={index}
                >
                  <Typography level='body-sm'>{referenceId}</Typography>
                </div>
              ))}
            </div>
            {errorMessage || isError ? (
              <Typography color='danger'>{errorMessage}</Typography>
            ) : (
              ''
            )}
          </div>
        )}
      </div>
    </div>
  );
}
